import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, 
  Stepper, Step, StepLabel, Box, Typography, TextField, 
  MenuItem, Divider, CircularProgress, Alert, Switch, FormControlLabel, Grid, Paper, Stack
} from '@mui/material';
import { Settings, FactCheck, Science,AddBusiness } from '@mui/icons-material';

const steps = ['Company Details', 'Gateway Config', 'Review'];

// --- Initial States Constants ---
const initialCompanyState = {
  cid: '',
  name: '',
  customer_type: '',
  db_name: '',
  soti_group_path: '',
  back_office: '',
  data_integration: '',
  import_export: '',
  country: '',
  logo: '',
  notes: ''
};

const initialProdConfig = {
  protocol: 'https',
  domain_name: '',
  server_port: 443,
  environment: 'PRODUCTION',
  android_slug: '',
  web_frontend_slug: '',
  api_backend_slug: '',
  current_app_version: '1.0.0',
  is_active: true
};

const initialTestConfig = {
  protocol: 'https',
  domain_name: '',
  server_port: 443,
  environment: 'TEST',
  android_slug: '',
  web_frontend_slug: '',
  api_backend_slug: '',
  current_app_version: '1.0.0',
  is_active: true
};

export const CreateCompanyModal = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creationStatus, setCreationStatus] = useState('');

  const [companyData, setCompanyData] = useState(initialCompanyState);
  const [includeTest, setIncludeTest] = useState(false);
  const [prodConfig, setProdConfig] = useState(initialProdConfig);
  const [testConfig, setTestConfig] = useState(initialTestConfig);

  // Function to clear all fields and reset the stepper
  const resetForm = () => {
    setActiveStep(0);
    setCompanyData(initialCompanyState);
    setProdConfig(initialProdConfig);
    setTestConfig(initialTestConfig);
    setIncludeTest(false);
    setError(null);
    setCreationStatus('');
  };

  useEffect(() => {
    if (companyData.cid) {
      setCompanyData(prev => ({ ...prev, db_name: `tms_${prev.cid}` }));
    }
  }, [companyData.cid]);

  const toggleTestEnv = (val) => {
    setIncludeTest(val);
    if (val) {
      setTestConfig(prev => ({
        ...prev,
        protocol: prodConfig.protocol,
        domain_name: prodConfig.domain_name,
        server_port: prodConfig.server_port,
      }));
    }
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleFinish = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      
      setCreationStatus('Provisioning Database...');
      const companyRes = await fetch('https://console.dreamtec.io/customers', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(companyData)
      });
      if (!companyRes.ok) throw new Error('Failed to create company record');
      const created = await companyRes.json();

      setCreationStatus('Setting up Production Config...');
      await fetch(`https://console.dreamtec.io/customers/${created.cid}/configs`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(prodConfig)
      });

      if (includeTest) {
        setCreationStatus('Setting up Test Config...');
        await fetch(`https://console.dreamtec.io/customers/${created.cid}/configs`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(testConfig)
        });
      }

      setLoading(false);
      resetForm(); // <--- RESET HAPPENS HERE
      onSuccess();
      onClose();
    } catch (err) {
      setLoading(false);
      setError(err.message);
    }
  };

  const isNextDisabled = () => {
    if (activeStep === 0) return !companyData.cid || !companyData.name || !companyData.customer_type;
    if (activeStep === 1) {
      const prodValid = prodConfig.domain_name && prodConfig.android_slug;
      const testValid = includeTest ? (testConfig.domain_name && testConfig.android_slug) : true;
      return !prodValid || !testValid;
    }
    return false;
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
                                          <Box 
  sx={{ 
    display: 'flex', 
    alignItems: 'flex-start', 
    gap: 1.5, 
    p: 2, 
    mb: 3, 
    bgcolor: 'rgba(47, 114, 124, 0.05)', 
    borderRadius: 2, 
    borderLeft: '4px solid #F6B93B' 
  }}
>
  <Typography variant="body2" sx={{ color: '#1A3A3F', lineHeight: 1.5, fontWeight: 500 }}>
    Use the <strong>Company ID (CID)</strong> value from the iMeter. For <strong>Food</strong> companies, use a unique number starting with the <strong>3000</strong> series.
  </Typography>
</Box>
            <Grid container spacing={2}>
              <Grid item xs={6}><TextField label="Company ID" placeholder="Enter Company ID" fullWidth required type="number" value={companyData.cid} onChange={(e) => setCompanyData({...companyData, cid: e.target.value})} /></Grid>
                            <Grid item xs={6}>

                <TextField select label="Customer Type" fullWidth required value={companyData.customer_type} onChange={(e) => setCompanyData({...companyData, customer_type: e.target.value})}>
                  <MenuItem value="FUEL">Fuel</MenuItem>
                  <MenuItem value="FOOD">Food</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}><TextField label="Company Name" fullWidth required value={companyData.name} onChange={(e) => setCompanyData({...companyData, name: e.target.value})} /></Grid>

              <Grid item xs={6}><TextField label="Database Name" fullWidth value={companyData.db_name} onChange={(e) => setCompanyData({...companyData, db_name: e.target.value})} /></Grid>
              <Grid item xs={6}><TextField label="Country" fullWidth value={companyData.country} onChange={(e) => setCompanyData({...companyData, country: e.target.value})} /></Grid>
              <Grid item xs={6}><TextField label="Back Office" fullWidth value={companyData.back_office} onChange={(e) => setCompanyData({...companyData, back_office: e.target.value})} /></Grid>
              <Grid item xs={6}><TextField label="Data Integration" fullWidth value={companyData.data_integration} onChange={(e) => setCompanyData({...companyData, data_integration: e.target.value})} /></Grid>
              <Grid item xs={12}><TextField label="SOTI Group Path" fullWidth value={companyData.soti_group_path} onChange={(e) => setCompanyData({...companyData, soti_group_path: e.target.value})} /></Grid>
              <Grid item xs={12}><TextField label="Notes" multiline rows={2} fullWidth value={companyData.notes} onChange={(e) => setCompanyData({...companyData, notes: e.target.value})} /></Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ py: 2 }}>
             <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#2F727C', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Settings fontSize="small" /> PRODUCTION GATEWAY
             </Typography>
             <Grid container spacing={2}>
                <Grid item xs={3}><TextField select label="Protocol" fullWidth value={prodConfig.protocol} onChange={(e) => setProdConfig({...prodConfig, protocol: e.target.value})}><MenuItem value="http">http</MenuItem><MenuItem value="https">https</MenuItem></TextField></Grid>
                <Grid item xs={6}><TextField label="Domain" fullWidth required value={prodConfig.domain_name} onChange={(e) => setProdConfig({...prodConfig, domain_name: e.target.value})} /></Grid>
                <Grid item xs={3}><TextField label="Port" fullWidth type="number" value={prodConfig.server_port} onChange={(e) => setProdConfig({...prodConfig, server_port: e.target.value})} /></Grid>
                <Grid item xs={4}><TextField label="Android Slug" fullWidth required value={prodConfig.android_slug} onChange={(e) => setProdConfig({...prodConfig, android_slug: e.target.value})} /></Grid>
                <Grid item xs={4}><TextField label="Web Slug" fullWidth value={prodConfig.web_frontend_slug} onChange={(e) => setProdConfig({...prodConfig, web_frontend_slug: e.target.value})} /></Grid>
                <Grid item xs={4}><TextField label="Backend Slug" fullWidth value={prodConfig.api_backend_slug} onChange={(e) => setProdConfig({...prodConfig, api_backend_slug: e.target.value})} /></Grid>
             </Grid>

             <FormControlLabel
                control={<Switch checked={includeTest} onChange={(e) => toggleTestEnv(e.target.checked)} />}
                label={<Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#64748B' }}>ENABLE TEST ENVIRONMENT</Typography>}
                sx={{ mt: 3, mb: 1 }}
             />

             {includeTest && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', borderStyle: 'dashed', borderColor: '#F6B93B' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#F6B93B', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Science fontSize="small" /> TEST GATEWAY
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={9}><TextField label="Test Domain" size="small" fullWidth value={testConfig.domain_name} onChange={(e) => setTestConfig({...testConfig, domain_name: e.target.value})} /></Grid>
                        <Grid item xs={3}><TextField label="Test Port" size="small" fullWidth value={testConfig.server_port} onChange={(e) => setTestConfig({...testConfig, server_port: e.target.value})} /></Grid>
                        <Grid item xs={4}><TextField label="Test Android Slug" size="small" fullWidth required value={testConfig.android_slug} onChange={(e) => setTestConfig({...testConfig, android_slug: e.target.value})} /></Grid>
                        <Grid item xs={4}><TextField label="Test Web Slug" size="small" fullWidth value={testConfig.web_frontend_slug} onChange={(e) => setTestConfig({...testConfig, web_frontend_slug: e.target.value})} /></Grid>
                        <Grid item xs={4}><TextField label="Test Backend Slug" size="small" fullWidth value={testConfig.api_backend_slug} onChange={(e) => setTestConfig({...testConfig, api_backend_slug: e.target.value})} /></Grid>
                    </Grid>
                </Paper>
             )}
          </Box>
        );
      case 2:
        return (
          <Box sx={{ py: 2 }}>
            <Box sx={{ bgcolor: '#f8fafc', p: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748B' }}>SUMMARY</Typography>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>{companyData.name}</Typography>
                <Typography variant="body2" color="text.secondary">Target Database: {companyData.db_name}</Typography>

                <Divider sx={{ my: 2 }} />
                
                <Stack direction="row" spacing={1}>
                    <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#2F727C', color: 'white', borderRadius: 1, fontSize: '0.65rem', fontWeight: 800 }}>PROD: {prodConfig.android_slug}</Box>
                    {includeTest && <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#F6B93B', color: 'white', borderRadius: 1, fontSize: '0.65rem', fontWeight: 800 }}>TEST: {testConfig.android_slug}</Box>}
                </Stack>
            </Box>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </Box>
        );
      default: return null;
    }
  };

  return (
    <Dialog open={open} onClose={loading ? null : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 900, p: 3, color: '#1A3A3F' }}>Onboard New Company</DialogTitle>
      <DialogContent dividers sx={{ px: 4 }}>
        <Stepper activeStep={activeStep} sx={{ py: 2 }}>
          {steps.map((label) => (
            <Step key={label}><StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.75rem', fontWeight: 800 } }}>{label}</StepLabel></Step>
          ))}
        </Stepper>
        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={() => { resetForm(); onClose(); }} disabled={loading} color="error">Cancel</Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep !== 0 && <Button onClick={handleBack} disabled={loading} color="primary">Back</Button>}
        <Button 
          variant="contained" 
          onClick={activeStep === steps.length - 1 ? handleFinish : handleNext}
          disabled={isNextDisabled() || loading}
          color="primary"
        >
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} color="inherit" />
                <Typography variant="caption" sx={{ fontWeight: 800, ml: 1 }}>{creationStatus}</Typography>
            </Stack>
          ) : (activeStep === steps.length - 1 ? 'Create' : 'Next')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};